// native
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

// addon
import { Subject } from 'rxjs';

// model
import { ToastButton, ToastConfig } from '../../models';

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  visibility$ = new Subject<boolean>();
  text$ = new Subject<string>();
  type$ = new Subject<string>();
  confirmButton$ = new Subject<ToastButton>();
  cancelButton$ = new Subject<ToastButton>();
  closeDisabled$ = new Subject<boolean>();
  isFullScreen$ = new Subject<boolean>();
  isCentered$ = new Subject<boolean>();

  closeDelay: number = 4000;
  autoClose: boolean = true;

  currentToastAutoClose: any;

  constructor(
    private sanitizer: DomSanitizer
  ) { }

  closeToast() {
    this.visibility$.next(false);
    this.text$.next('');
  }

  openToast(config: ToastConfig) {
    this.visibility$.next(true);
    this.text$.next(config.text);

    if (config.type && config.type === 'success') {
      this.type$.next('toast--success');
    } else {
      this.type$.next('toast--error');
    }

    if (!!config.closeDisabled) {
      this.closeDisabled$.next(true);
    } else {
      this.closeDisabled$.next(false);
    }

    this.confirmButton$.next(config.confirmButton);
    this.cancelButton$.next(config.cancelButton);

    this.isFullScreen$.next(!!config.isFullScreen);
    this.isCentered$.next(!!config.isCentered);

    this.currentToastAutoClose && clearTimeout(this.currentToastAutoClose);

    if (config.autoClose === false) {
      return;
    }

    this.currentToastAutoClose = setTimeout(() => {
      this.closeToast();
    }, config.closeDelay || this.closeDelay);
  }

  showGenericError() {
    this.openToast({
      text: 'There was an error while performing this action.',
      type: 'error'
    });
  }

  showNoInternet() {
    this.openToast({
      text: 'SmartCite requires internet access. Please connect or verify your connection to the internet.',
      type: 'error'
    });
  }

  showCslError(count: number) {
    const message = `Critical citation information missing. Please update the highlighted reference${count > 1 ? 's' : ''} with more details in your library or choose a different style. 
    ${count > 1 ? 'These' : 'This'} reference${count > 1 ? 's' : ''} caused an error in ${count.toString()} citation${count > 1 ? 's' : ''}. 
    For more information please click <a href="https://support.papersapp.com/en/support/solutions/articles/30000042442-troubleshooting-metadata-error-detected" target="_blank">here</a>.`;

    this.openToast({
      text: <string>this.sanitizer.bypassSecurityTrustHtml(message),
      type: 'error',
      autoClose: false
    });
  }

  showWordOnlineError() {
    this.openToast({
      text: `Unfortunately, this action is not supported in Word Online.`,
      type: 'error',
      closeDelay: 6000
    });
  }

  showOverrideResetInfo() {
    this.openToast({
      text: `The citation text in the document has been reset to the placeholder value. 
        With the next bibliography reformat it will be formatted in the selected style.`,
      type: 'success',
      closeDelay: 6000
    });
  }
}